import CheckIcon from '@mui/icons-material/Check';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Typography } from '@mui/material';
import React from 'react';

interface SectionCardProps {
  title: string;
  icon?: React.ReactNode;
  /** Optional meta, e.g. "5 fields", "Round 1 of 2". */
  meta?: string;
  /** Whether this section is currently in edit mode. */
  editing?: boolean;
  /** Omit to make the section read-only (no Edit affordance). */
  onEdit?: () => void;
  onCancel?: () => void;
  onSave?: () => void;
  /** Extra class on the card, for a page that lays its sections out in a grid. */
  className?: string;
  children: React.ReactNode;
}

/**
 * A section card with per-section inline editing. Only one section edits at a
 * time (the parent enforces it); the border turns accent while editing.
 */
const SectionCard: React.FC<SectionCardProps> = ({
  title,
  icon,
  meta,
  editing = false,
  onEdit,
  onCancel,
  onSave,
  className,
  children,
}) => {
  return (
    <Box
      className={`cc-section${editing ? ' is-editing' : ''}${className ? ` ${className}` : ''}`}
    >
      <Box className="cc-section__header">
        <Box className="cc-section__title-wrap">
          {icon && <span className="cc-section__icon">{icon}</span>}
          <Typography component="h3" className="cc-section__title">
            {title}
          </Typography>
          {meta && <span className="cc-section__meta">{meta}</span>}
        </Box>

        {onEdit && (
          <Box className="cc-section__actions">
            {editing ? (
              <>
                <button type="button" className="cc-section__btn cc-section__btn--ghost" onClick={onCancel}>
                  Cancel
                </button>
                <button type="button" className="cc-section__btn cc-section__btn--primary" onClick={onSave}>
                  <CheckIcon className="cc-section__btn-icon" />
                  Save section
                </button>
              </>
            ) : (
              <button type="button" className="cc-section__edit" onClick={onEdit}>
                <EditOutlinedIcon className="cc-section__btn-icon" />
                Edit
              </button>
            )}
          </Box>
        )}
      </Box>

      <Box className="cc-section__body">{children}</Box>
    </Box>
  );
};

export default SectionCard;

/* ---- Field helper: label + read-only value or an edit slot ---- */
interface WorkspaceFieldProps {
  label: string;
  /** Read-only value (shown when `editing` is false or no `edit` given). */
  value?: React.ReactNode;
  /** Edit control (shown when `editing` is true). */
  edit?: React.ReactNode;
  editing?: boolean;
  /** Span the full row (long fields). */
  full?: boolean;
  mono?: boolean;
  valueColor?: string;
}

/**
 * The label above a field in a workspace grid.
 *
 * <p>Two label specs exist in the console and both are wanted. `FieldLabel` (`.cc-field-label`,
 * 13px sentence case) belongs to forms and dialogs, where a label sits above a control the user
 * came to fill in. This one is the workspace grid's own: 10px uppercase with wide tracking, sized
 * to disappear next to its value, because a workspace is dozens of read-mostly rows and a
 * form-sized label on each of them would compete with the data.
 *
 * <p>Which to use follows from that. A read-only row, or a row whose editor appears in place, takes
 * this one. A composer the user fills in from empty, like the feedback box on an application, takes
 * `FieldLabel`. The application card was showing both at once, which is what made it look wrong.
 *
 * <p>It is a component rather than a class so the spec is written once. It was written out as a
 * bare `<span className="cc-field__label">` in six places, which is six chances to drift.
 */
export const WorkspaceFieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="cc-field__label">{children}</span>
);

export const WorkspaceField: React.FC<WorkspaceFieldProps> = ({
  label,
  value,
  edit,
  editing = false,
  full = false,
  mono = false,
  valueColor,
}) => (
  <Box className={`cc-field${full ? ' cc-field--full' : ''}`}>
    <WorkspaceFieldLabel>{label}</WorkspaceFieldLabel>
    {editing && edit ? (
      edit
    ) : (
      <span
        className={`cc-field__value${mono ? ' cc-field__value--mono' : ''}`}
        style={valueColor ? { color: valueColor } : undefined}
      >
        {/*
          * An empty string is "no value", the same as null.
          *
          * This was `value ?? '-'`, and `??` only catches null and undefined -- so a field
          * holding `''` rendered as literal nothing while its neighbours rendered a dash. Most
          * workspace forms coerce every null to `''` when they hydrate, which means the common
          * case produced the blank one: an admin looking at Seniority or Education could not
          * tell an unset field from a field the page had failed to load.
          */}
        {value === '' || value == null ? '-' : value}
      </span>
    )}
  </Box>
);
